import React, { useState } from 'react';
import { Radio, Flex, createStyles, Divider } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { showNotification } from '@mantine/notifications';
import { useRouter } from 'next/router';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import type { AutoPickStrategy } from '~/services/contests/interfaces/contest-auto-picks';

import { useUpdateAutoPickStrategy } from '../../../../../common/hooks/useUpdateAutoPickStrategy';

const useStyles = createStyles((theme) => ({
  container: {
    width: 440,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto',
    padding: theme.other.spacing._24,
  },
  radio: {
    width: '100%',
  },
  radioBody: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._16,
  },
  radioInner: {
    alignSelf: 'center',
  },
  radioRadio: {
    alignSelf: 'center',
  },
  description: {
    display: 'flex',
    textAlign: 'center',
  },
  optionalDescription: {
    alignSelf: 'stretch',
  },
}));

export interface GamecenterAutoPickEditSheetProps {
  isOpen: boolean;
  onClose: () => void;
  contestId: string;
  description: string;
  title: string;
  currentStrategy?: string;
  availableStrategies: AutoPickStrategy[];
  redirectTo?: string;
  isLoading?: boolean;
}

function GamecenterAutoPickEditSheet({
  isOpen,
  onClose,
  contestId,
  currentStrategy,
  redirectTo,
  availableStrategies,
  isLoading = false,
  description,
  title,
}: GamecenterAutoPickEditSheetProps) {
  const { t } = useTranslation('contest');
  const router = useRouter();
  const { classes, theme } = useStyles();
  const [selectedStrategy, setSelectedStrategy] = useState<string>(currentStrategy || '');

  const updateAutoPickMutation = useUpdateAutoPickStrategy();

  const handleSave = async () => {
    try {
      await updateAutoPickMutation.mutateAsync({
        contestId,
        strategy: selectedStrategy,
      });

      if (redirectTo) {
        router.push(redirectTo);
      }
      onClose();
    } catch (error) {
      showNotification({
        title: t('gamecenter.modal.gamecenterAutoPickEditSheet.error.title'),
        message: t('gamecenter.modal.gamecenterAutoPickEditSheet.error.message'),
        color: 'orange.3',
        autoClose: 5000,
      });
    }
  };

  return (
    <Dialog
      className={classes.container}
      type={{ desktop: 'modal', mobile: 'drawer' }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <DialogBody className={classes.body}>
        <Title
          variant="headline-large"
          ta="center"
          mb={theme.spacing.md}
          color={theme.colors.gray2[7]}
        >
          {title}
        </Title>

        <Text variant="body-large" className={classes.description} color={theme.colors.gray2[3]}>
          {description}
        </Text>

        <Radio.Group value={selectedStrategy} onChange={setSelectedStrategy}>
          <Flex direction="column" gap={theme.spacing.md} w="100%" maw={392} mt={theme.spacing.md}>
            {availableStrategies.map((strategy) => (
              <>
                <Radio
                  pt={theme.other.spacing._4}
                  pb={theme.other.spacing._4}
                  key={strategy.value}
                  value={strategy.value}
                  className={classes.radio}
                  classNames={{
                    body: classes.radioBody,
                    inner: classes.radioInner,
                    radio: classes.radioRadio,
                  }}
                  label={
                    <Flex direction="column" gap={theme.other.spacing._8}>
                      <Text variant="headline-medium" color={theme.colors.gray2[5]}>
                        {strategy.label}
                      </Text>
                      {strategy.description && (
                        <Text
                          variant="body-small"
                          color={theme.colors.gray2[3]}
                          className={classes.optionalDescription}
                        >
                          {strategy.description}
                        </Text>
                      )}
                    </Flex>
                  }
                  size="sm"
                  disabled={isLoading}
                />

                <Divider color="gray2.0" size="xs" />
              </>
            ))}
          </Flex>
        </Radio.Group>

        <Button
          variant="secondary-fill"
          size="medium"
          fullWidth
          onClick={handleSave}
          disabled={!selectedStrategy || updateAutoPickMutation.isLoading || isLoading}
          loading={updateAutoPickMutation.isLoading}
          style={{ marginTop: theme.spacing.xl }}
        >
          {t('gamecenter.modal.gamecenterAutoPickEditSheet.save')}
        </Button>
      </DialogBody>
    </Dialog>
  );
}

export default GamecenterAutoPickEditSheet;
